import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import getActivityTypeConf from 'src/modules/activities/ActivityTypesConf';
import typeLabel from '@/constants/ActivityTypeLabelsStudents';
import getActivity from '@/api/content/getActivity.endpoint';
import createProgress from '@/api/progress/createProgress.endpoint';
import updateProgress from '@/api/progress/updateProgress.endpoint';
import getStudentTaskDetail from '@/api/studentDashboard/getStudentTaskDetail.endpoint';
import expandTaskMiniactivities, {
  getMiniactivityProgress,
  getTriesProgresses,
} from 'src/modules/activities/expandMiniactivies';
import getDefaultLevel from 'src/modules/tasks/getDefaultLevel';
import getTask from '@/api/teacher/getTask.endpoint';
import { FullscreenContext } from '../FullscreenContext/FullscreenContext';
import findOrCreateProgress from '@/api/progress/findOrcreateProgress.endpoint';
import { isActivityDone } from './TaskActivityNav/TaskActivityNav';
import { GlobalContext } from '../GlobalContext/GlobalContext';
import * as Sentry from '@sentry/nextjs';
import getStudentActivityProgress from '@/modules/progress/calculations/studentActivityProgress';

export default function useTaskActivityDetail({
  activityId,
  taskActivityId,
  miniactivityConf,
  taskId,
  goToNextTaskActivity,
  isPreview,
}) {
  const router = useRouter();
  const fullscreenContext = useContext(FullscreenContext);
  const globalContext = useContext(GlobalContext);
  const [state, setState] = useState({
    loadingActivity: true,
    loadingTaskActivities: true,
    activity: null,
    taskActivity: null,
    activityConf: null,
    progressId: null,
    initialTaskProgress: null,
    taskActivities: null,
    defaultLevel: null,
    showFeedback: false,
    savingProgress: false,
    syncError: false,
    restartActivity: false,
    dueDate: null,
    currentTaskActivity: null,
    isTaskActivityDone: false,
  });

  const actions = {
    setState: (newState) => {
      setState((previousState) => ({ ...previousState, ...newState }));
    },
    loadActivity: async () => {
      actions.setState({
        loadingActivity: true,
        progressId: null,
      });
      let activity;
      try {
        activity = await getActivity(activityId);
      } catch (error) {
        actions.setState({ loadingActivity: false });
        openAPIErrorNotification({
          title: 'There was an error',
          description: <>There was a problem loading the activity.</>,
          errorFeedback: error.feedbackData,
        });
      }
      let activityConf;
      if (activity && miniactivityConf) {
        activityConf = getActivityConf({
          miniactivityConf,
          type: activity.type,
          subType: activity.data.subType,
        });
      }
      actions.setState({
        loadingActivity: false,
        activity,
        activityConf,
      });
    },
    loadTaskActivities: async () => {
      actions.setState({ loadingTaskActivities: true });
      try {
        const task = isPreview
          ? await getTask(taskId)
          : await getStudentTaskDetail(taskId);
        task.taskActivities?.sort((a, b) => {
          return a.order - b.order;
        });
        const taskActivities = expandTaskMiniactivities(task.taskActivities);
        actions.setState({
          loadingTaskActivities: false,
          taskActivities,
          defaultLevel: getDefaultLevel(task.class),
          dueDate: task.dueDate,
        });
      } catch (error) {
        actions.setState({
          loadingTaskActivities: false,
        });
        openAPIErrorNotification({
          title: 'There was an error',
          description: <>There was a problem loading the activity.</>,
          errorFeedback: error.feedbackData,
        });
      }
    },
    refreshProgress: (progressData) => {
      try {
        actions.setState({
          taskActivities: state.taskActivities.map((taskActivity) => {
            if (
              taskActivity.id == taskActivityId &&
              taskActivity.activityConf.miniactivityType ==
                state.activityConf.miniactivityType
            ) {
              const progresses = [...taskActivity.progresses];
              const progressIndex = progresses.findIndex(
                (p) => p.id == state.progressId
              );
              if (progressIndex == -1) {
                progresses.push(progressData);
              } else {
                progresses[progressIndex] = progressData;
              }
              const activityProgress = getStudentActivityProgress({
                ...taskActivity,
                progresses,
              });

              return {
                ...taskActivity,
                activityProgress: getMiniactivityProgress(
                  activityProgress,
                  miniactivityConf
                ),
                progresses: getTriesProgresses(progresses, miniactivityConf),
              };
            } else {
              return taskActivity;
            }
          }),
        });
      } catch (error) {
        console.log(error);
        Sentry.captureException(error, {
          extra: {
            where: 'refreshProgress',
            user: globalContext.state.user,
            progressId: state.progressId,
            taskActivityId,
            miniactivityConf,
          },
        });
      }
    },
    feedbackModalActions: {
      repeat: () => {
        actions.setState({ restartActivity: true });
      },
      goBack: () => {
        router.push(`/student/dashboard`);
      },
      closeFeedback: () => {
        actions.setState({
          showFeedback: false,
          savingProgress: false,
          syncError: false,
        });
        goToNextTaskActivity();
      },
    },
    taskProgressActions: {
      createProgress: async (progressData, continueFromLastProgress) => {
        try {
          actions.setState({ progressId: null });
          let initialTaskProgress = null;
          let progressId = null;
          if (continueFromLastProgress) {
            const progress = await findOrCreateProgress({
              taskActivityId,
              miniactivityType: state.activityConf.miniactivityType,
              progressData,
            });
            progressId = progress.id;
            initialTaskProgress = progress.data;
          } else {
            const progress = await createProgress({
              taskActivityId,
              progressData,
            });
            progressId = progress.id;
          }

          actions.setState({ progressId, initialTaskProgress });
        } catch (error) {
          openAPIErrorNotification({
            title: 'There was error creating the progress record',
            description: (
              <>
                When this happens <strong>your progress won't be saved</strong>,
                even if you finish the activity. Please{' '}
                <strong>reload the page</strong>.
              </>
            ),
            errorFeedback: error.feedbackData,
          });
          Sentry.captureException(error, {
            extra: {
              where: 'createProgress',
              user: globalContext.state.user,
              taskActivityId,
              progressData,
            },
          });
        }
      },
      updateProgress: async (progressData) => {
        if (state.progressId) {
          const showFeedback =
            progressData?.progress == 100 || progressData.gameOver;
          let updatedProgress;
          try {
            if (showFeedback) {
              actions.setState({
                showFeedback: true,
                savingProgress: true,
                syncError: false,
              });
            }
            updatedProgress = await updateProgress({
              progressId: state.progressId,
              taskActivityId,
              progressData,
            });
          } catch (error) {
            console.log(error);
            openAPIErrorNotification({
              title: 'There was error updating the progress record',
              description: (
                <>
                  Your progress has not been saved.{' '}
                  <strong>Reload the page and try again</strong>. If this
                  continues happening tell your teacher to contact us.
                </>
              ),
              errorFeedback: error.feedbackData,
            });
            Sentry.captureException(error, {
              extra: {
                where: 'updateProgress',
                user: globalContext.state.user,
                taskActivityId,
                progressData,
              },
            });
          }
          if (showFeedback) {
            await actions.loadTaskActivities();
            fullscreenContext.actions.setFullscreen(false);
            actions.setState({
              showFeedback: true,
              savingProgress: false,
              syncError: !!updatedProgress?.syncError,
            });
          } else if (updatedProgress) {
            actions.refreshProgress(updatedProgress);
          }
        }
      },
      onActivityRestarted: () => {
        actions.setState({
          restartActivity: false,
          showFeedback: false,
          savingProgress: false,
          syncError: false,
        });
      },
      goToNextTaskActivity: () => {
        actions.feedbackModalActions.closeFeedback();
      },
    },
  };

  useEffect(() => {
    if (
      activityId &&
      miniactivityConf &&
      (activityId != state.activity?.id ||
        miniactivityConf.miniactivityType !=
          state.activityConf?.miniactivityType ||
        miniactivityConf.level != state.activityConf?.level)
    ) {
      actions.loadActivity();
    }
  }, [activityId, state.activity, miniactivityConf, state.activityConf]);

  useEffect(() => {
    if (taskId && !state.taskActivities) {
      actions.loadTaskActivities();
    }
  }, [taskId, state.taskActivities]);

  useEffect(() => {
    if (state.taskActivities && state.activity) {
      const currentTaskActivity = state.taskActivities.find(
        (taskActivity) =>
          taskActivity.id == taskActivityId &&
          taskActivity.activityId == state.activity.id &&
          taskActivity.activityConf.miniactivityType ==
            state.activityConf.miniactivityType
      );
      actions.setState({
        currentTaskActivity,
        isTaskActivityDone: isActivityDone(
          currentTaskActivity?.activityProgress
        ),
      });
    }
  }, [state.taskActivities, state.activity, taskActivityId]);

  return { state, actions };
}

function getActivityConf({ miniactivityConf, type, subType }) {
  const activityTypeConf = getActivityTypeConf({
    type,
    subType,
    miniactivityType: miniactivityConf.miniactivityType,
  });
  const activityConf = {
    label: typeLabel(type, subType, miniactivityConf.miniactivityType),
    level: miniactivityConf.level,
    themes: miniactivityConf.themes,
    progress: activityTypeConf.progress,
    correctness: activityTypeConf.correctness,
    canLoadProgress: activityTypeConf.canLoadProgress,
    miniactivityTypeKey: activityTypeConf.miniactivityTypeKey,
    miniactivityType: activityTypeConf.miniactivityType,
  };
  return activityConf;
}
